import React, { useEffect, useRef, useState } from "react";
import { X, Download, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

const MIN = 1, MAX = 5;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

/** Fullscreen photo viewer with pinch-to-zoom, double-tap zoom, drag-to-pan and mouse-wheel zoom. */
export default function ChatMediaViewer({ src, alt = "", onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);
  const state = useRef({ scale: 1, pos: { x: 0, y: 0 } });
  state.current = { scale, pos };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const apply = (s, p) => {
    const ns = clamp(s, MIN, MAX);
    const np = ns === 1 ? { x: 0, y: 0 } : p;
    setScale(ns); setPos(np);
  };

  const zoomAt = (factor, cx, cy, el) => {
    const { scale: s, pos: p } = state.current;
    const rect = el.getBoundingClientRect();
    const ox = cx - rect.left - rect.width / 2;
    const oy = cy - rect.top - rect.height / 2;
    const ns = clamp(s * factor, MIN, MAX);
    const k = ns / s;
    apply(ns, { x: ox - (ox - p.x) * k, y: oy - (oy - p.y) * k });
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        lastTap.current = 0;
        if (state.current.scale > 1) apply(1, { x: 0, y: 0 });
        else zoomAt(2.5, e.clientX, e.clientY, e.currentTarget);
        return;
      }
      lastTap.current = now;
      gesture.current = { type: "pan", start: { x: e.clientX, y: e.clientY }, origin: { ...state.current.pos } };
    } else if (pts.length === 2) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      gesture.current = { type: "pinch", startDist: d, startScale: state.current.scale, startPos: { ...state.current.pos },
        center: { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 } };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    const pts = [...pointers.current.values()];
    if (g.type === "pinch" && pts.length >= 2) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const ns = clamp(g.startScale * (d / g.startDist), MIN, MAX);
      const c = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      apply(ns, { x: g.startPos.x + (c.x - g.center.x), y: g.startPos.y + (c.y - g.center.y) });
    } else if (g.type === "pan" && state.current.scale > 1) {
      apply(state.current.scale, { x: g.origin.x + (e.clientX - g.start.x), y: g.origin.y + (e.clientY - g.start.y) });
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
    else if (pointers.current.size === 1) {
      const [p] = [...pointers.current.values()];
      gesture.current = { type: "pan", start: { x: p.x, y: p.y }, origin: { ...state.current.pos } };
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY, e.currentTarget);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col select-none" data-testid="chat-media-viewer" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between px-3 py-2 text-white shrink-0">
        <span className="text-xs opacity-70" data-testid="chat-media-viewer-zoom">{Math.round(scale * 100)}%</span>
        <div className="flex items-center gap-1">
          <button type="button" className="p-2 rounded-full hover:bg-white/10" onClick={(e) => zoomAt(1 / 1.3, window.innerWidth / 2, window.innerHeight / 2, e.currentTarget.closest("[data-testid=chat-media-viewer]").querySelector("[data-stage]"))} aria-label="Reduzir" data-testid="chat-media-viewer-zoom-out"><ZoomOut className="w-5 h-5" /></button>
          <button type="button" className="p-2 rounded-full hover:bg-white/10" onClick={(e) => zoomAt(1.3, window.innerWidth / 2, window.innerHeight / 2, e.currentTarget.closest("[data-testid=chat-media-viewer]").querySelector("[data-stage]"))} aria-label="Ampliar" data-testid="chat-media-viewer-zoom-in"><ZoomIn className="w-5 h-5" /></button>
          <button type="button" className="p-2 rounded-full hover:bg-white/10" onClick={() => apply(1, { x: 0, y: 0 })} aria-label="Restaurar" data-testid="chat-media-viewer-reset"><RotateCcw className="w-5 h-5" /></button>
          <a href={src} download target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-white/10" aria-label="Baixar" data-testid="chat-media-viewer-download"><Download className="w-5 h-5" /></a>
          <button type="button" className="p-2 rounded-full hover:bg-white/10" onClick={onClose} aria-label="Fechar" data-testid="chat-media-viewer-close"><X className="w-6 h-6" /></button>
        </div>
      </div>
      <div
        data-stage
        className="flex-1 overflow-hidden flex items-center justify-center touch-none"
        style={{ cursor: scale > 1 ? "grab" : "zoom-in" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onClick={(e) => { if (e.target === e.currentTarget && scale === 1) onClose(); }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          data-testid="chat-media-viewer-img"
          className="max-w-full max-h-full object-contain will-change-transform"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, transition: gesture.current ? "none" : "transform 120ms ease-out" }}
        />
      </div>
      <div className="text-center text-[11px] text-white/60 pb-2 shrink-0">Pinça para ampliar · toque duplo para zoom · arraste para mover</div>
    </div>
  );
}
